/**
 * domains/attribution/attributionRuntime.ts — MER Algo Footprints v8.2.0
 *
 * Runtime integration bridge — connects the attribution domain to app flow.
 *
 * Design principles:
 *   - ADDITIVE ONLY: no engine mutation, no import cycles
 *   - The state engine (buildSymbolContext) remains pure
 *   - This module is the sole integration surface between runtime and attribution
 *   - All calls are best-effort (errors are logged, never thrown to callers)
 *
 * Integration points (two only):
 *   1. captureFromContext(ctx) — called at the getSymbolContext cache-miss boundary.
 *      Builds + journals an AttributionSnapshot from the fresh SymbolContext.
 *      Deduplicates: same symbol/state/candleCount → no-op.
 *
 *   2. evaluatePendingForSymbol(symbol, candles) — called after setCandles updates
 *      the store. Passes forward bars to processPending for the given symbol.
 *      Idempotent: evaluated snapshots are skipped by the processor.
 *
 * No circular dependency risk: this module imports from attribution/* only.
 * The callers (symbolContext.ts, store) import this module — not vice versa.
 */

import { capture, processPending } from './attributionProcessor';
import { getJournalStats, getLatestRecord } from './attributionJournal';
import type { Candle } from '../../shared/types';

// ─── Deduplication guard ──────────────────────────────────────────────────────
// Prevents repeated capture for the same symbol/state/candleCount combination.
// Key: `${symbol}:${state}:${candleCount}`
// Cleared when state changes — keeps the Set from growing unboundedly.

const _capturedKeys = new Set<string>();
const MAX_KEY_AGE_MS = 60 * 60 * 1000; // 1 hour — clean up stale keys
const _keyTimestamps = new Map<string, number>();

function _makeKey(symbol: string, state: string, candleCount: number): string {
  return `${symbol}:${state}:${candleCount}`;
}

function _pruneStaleKeys(): void {
  const cutoff = Date.now() - MAX_KEY_AGE_MS;
  for (const [key, ts] of _keyTimestamps.entries()) {
    if (ts < cutoff) {
      _capturedKeys.delete(key);
      _keyTimestamps.delete(key);
    }
  }
}

// ─── Type shim — avoids importing SymbolContext (would create a cycle risk) ────

interface ContextForCapture {
  symbol:       string;
  candles:      Candle[];
  stateTransition: {
    state:           string;
    actionability:   string;
    directionalBias: string;
    confidence:      number;
    setupQuality:    number;
    contextFlags:    string[];
    triggers:        { longTrigger?: string; shortTrigger?: string; confirmation?: string[]; failure?: string[] };
    invalidation:    string[];
    engineVersion:   string;
    evaluatedAt:     number;
    rationale:       string[];
    operatorGuidance:string;
    scoreFactors:    Record<string, number>;
  };
  curPrice:     number;
  curVWAP:      number | null;
  regime:       { label?: string; trend?: string; momentum?: string } | null;
  gamma:        { regime?: string; volRisk?: string; confidence?: string } | null;
  zones:        Array<{ price: number; label: string; type: string }>;
  interval:     string;
}

// ─── A. Capture from runtime context ─────────────────────────────────────────

/**
 * Called at the getSymbolContext cache-miss boundary.
 * Captures an AttributionSnapshot from the freshly-computed SymbolContext.
 *
 * Silently skips if:
 *   - candleCount < 10 (not enough data to be meaningful)
 *   - same symbol/state/candleCount already captured (dedup)
 *   - state is NO_VALID_SETUP (not worth tracking)
 */
export function captureFromContext(ctx: ContextForCapture): void {
  try {
    const { symbol, stateTransition: st, candles, curPrice, interval } = ctx;
    const candleCount = candles.length;

    // Skip trivial / insufficient states
    if (st.state === 'NO_VALID_SETUP') return;
    if (candleCount < 10) return;

    // Dedup check
    const key = _makeKey(symbol, st.state, candleCount);
    if (_capturedKeys.has(key)) return;

    // Prune stale keys periodically
    if (_capturedKeys.size > 500) _pruneStaleKeys();

    _capturedKeys.add(key);
    _keyTimestamps.set(key, Date.now());

    // Build key levels from zones
    const support    = ctx.zones.filter(z => z.type === 'support').map(z => z.price).slice(0, 3);
    const resistance = ctx.zones.filter(z => z.type === 'resistance').map(z => z.price).slice(0, 3);

    capture({
      symbol,
      timeframe:   interval,
      entryPrice:  curPrice,
      emittedAt:   Date.now(),
      stateResult: {
        state:           st.state as any,
        actionability:   st.actionability as any,
        directionalBias: st.directionalBias as any,
        confidence:      st.confidence,
        setupQuality:    st.setupQuality,
        contextFlags:    st.contextFlags,
        triggers:        st.triggers,
        invalidation:    st.invalidation,
        engineVersion:   st.engineVersion,
        evaluatedAt:     st.evaluatedAt,
        rationale:       st.rationale ?? [],
        operatorGuidance:st.operatorGuidance ?? '',
        scoreFactors:    st.scoreFactors as any,
      },
      keyLevels: {
        support,
        resistance,
        vwap: ctx.curVWAP ?? null,
      },
      regimeContext: ctx.regime
        ? { label: ctx.regime.label, trend: ctx.regime.trend, momentum: ctx.regime.momentum }
        : undefined,
      gammaContext: ctx.gamma
        ? { regime: ctx.gamma.regime, volRisk: ctx.gamma.volRisk, confidence: ctx.gamma.confidence }
        : undefined,
    });
  } catch (_err) {
    // Best-effort — attribution must never crash the runtime context build
  }
}

// ─── B. Evaluate pending for a symbol ────────────────────────────────────────

/**
 * Called after setCandles delivers new bars for a symbol.
 * Evaluates any pending snapshots for that symbol that now have enough bars.
 * Best-effort: errors are caught and silently discarded.
 *
 * @param symbol  — uppercase symbol
 * @param candles — full candle array (chronological, newest last)
 * @param minBars — minimum forward bars required before evaluating (default 5)
 */
export function evaluatePendingForSymbol(
  symbol:  string,
  candles: Candle[],
  minBars  = 5,
): void {
  try {
    if (!candles.length) return;
    const symbolBars = new Map([[symbol, candles]]);
    processPending(symbolBars, minBars);
  } catch (_err) {
    // Best-effort — must never disrupt bar processing
  }
}

// ─── C. Runtime status surface ────────────────────────────────────────────────

export interface AttributionRuntimeStatus {
  journalStats: ReturnType<typeof getJournalStats>;
  latestRecord: ReturnType<typeof getLatestRecord>;
  capturedKeyCount: number;
}

/**
 * Returns a lightweight attribution status snapshot for diagnostic surfaces.
 * Safe to call from any component — never throws.
 */
export function getAttributionRuntimeStatus(): AttributionRuntimeStatus {
  try {
    return {
      journalStats:     getJournalStats(),
      latestRecord:     getLatestRecord(),
      capturedKeyCount: _capturedKeys.size,
    };
  } catch {
    return {
      journalStats:     { total: 0, pending: 0, evaluated: 0, expired: 0 },
      latestRecord:     undefined,
      capturedKeyCount: 0,
    };
  }
}

/** Clear dedup keys (for testing) */
export function _resetCapturedKeys(): void {
  _capturedKeys.clear();
  _keyTimestamps.clear();
}
